import React, { Fragment, FunctionComponent, useState, useEffect } from 'react';
import styled, { withTheme, DefaultTheme } from 'styled-components';
import { Form, Field, FormSpy } from 'react-final-form';
import { useRouter } from 'next/router';
import { Box, InputField, SelectField, Text } from 'mdlkit';
import { FormattedMessage, useIntl } from 'react-intl';
import { PrimaryForm, MemberId, AllowDepReg } from './InsuranceForm';
import { AffiliationSearchField } from '../../containers/register';
import Gender from './Gender';
import { StyledFlex, FormSubmitLoadingButton } from '../shared/Styles';
import { validateDob } from '../../utils/register/validations';
import {
  composeValidators,
  required,
  zipCode,
} from '../../utils/shared/validations';
import {
  normalizeNumbersOnly,
  normalizeZipCode,
} from '../../utils/register/normalize';
import { EligibilityCheckResponse, DobType } from '../../interfaces/register';
import { DTC, CSV } from '../../constants/register';
import { MONTHS } from '../../constants/months';
import { DOBGroup } from './PatientForm';

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: ${({ theme }) => theme.space.xs + theme.space.sm}px;
`;

const StyledText = styled(Text)<{ htmlFor?: string }>`
  align-self: self-start;
  padding-top: ${({ theme }) => theme.space.xs + theme.space.sm}px;
`;

interface Props {
  onSubmit: (values: any) => void;
  initialValues?: EligibilityCheckResponse;
  disableSubmit: boolean;
  theme: DefaultTheme;
  setShowSkipLink(boolean): void;
}

const ReviewForm: FunctionComponent<Props> = ({
  onSubmit,
  initialValues,
  disableSubmit,
  theme,
  setShowSkipLink,
}: Props) => {
  const router = useRouter();
  const isBranded = typeof router.query.affiliation !== 'undefined';
  const { formatMessage: f } = useIntl();

  const [hideMemberIdForm, setHideMemberIdForm] = useState<boolean>(true);
  const [
    hasAffiliationSearchError,
    setHasAffiliationSearchError,
  ] = useState<boolean>(false);

  const [insuranceFormValues, setInsuranceFormValues] = useState<any>(null);

  useEffect(() => {
    if (typeof initialValues !== 'undefined') {
      setHideMemberIdForm(
        initialValues.affiliationType === DTC ||
          initialValues.affiliationType === CSV
      );
    }
  }, []);

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={(values: any) => {
        let errors: {
          isDependent?: string;
          relationship?: string;
          primaryGender?: string;
        } & DobType = {};

        if (!values.isDependent) {
          errors.isDependent = f({ id: 'validations.required' });
        }
        if (values.isDependent === 'true') {
          if (!values.relationship) {
            errors.relationship = f({ id: 'validations.required' });
          }
          if (!values.primaryGender) {
            errors.primaryGender = f({ id: 'validations.required' });
          }
        }

        errors = {
          ...errors,
          ...validateDob(values),
        };
        return errors;
      }}
      render={({ valid, submitting, handleSubmit }) => (
        <StyledForm onSubmit={handleSubmit} spellCheck={false}>
          <StyledFlex>
            <Box>
              <Field
                id="firstName"
                label={f({ id: 'firstName' })}
                name="firstName"
                validate={required}
                component={InputField}
                autoCorrect="off"
                spellCheck={false}
              />
            </Box>
            <Box>
              <Field
                id="lastName"
                label={f({ id: 'lastName' })}
                name="lastName"
                validate={required}
                component={InputField}
                autoCorrect="off"
                spellCheck={false}
              />
            </Box>
          </StyledFlex>

          <Field
            pt={theme.space.sm + theme.space.xs}
            id="zip"
            label={f({ id: 'homeZipCode' })}
            name="zip"
            validate={composeValidators(required, zipCode)}
            component={InputField}
            parse={normalizeZipCode}
            aria-label="home zip code"
          />

          <DOBGroup mt={theme.space.sm + theme.space.xs}>
            <Box>
              <Field
                id="dobMonth"
                label={f({ id: 'month' })}
                name="dobMonth"
                validate={required}
                component={SelectField}
                blank
                options={MONTHS}
              />
            </Box>
            <Box>
              <Field
                id="dobDay"
                label={f({ id: 'day' })}
                name="dobDay"
                validate={required}
                component={InputField}
                maxLength={2}
                parse={normalizeNumbersOnly}
              />
            </Box>
            <Box>
              <Field
                id="dobYear"
                label={f({ id: 'year' })}
                name="dobYear"
                validate={required}
                component={InputField}
                maxLength={4}
                parse={normalizeNumbersOnly}
              />
            </Box>
          </DOBGroup>

          <StyledText
            as="label"
            htmlFor="gender"
            pt={theme.space.sm + theme.space.xs}
            pb="xs"
          >
            <FormattedMessage id="register.gender" />
          </StyledText>
          <Gender />

          {!isBranded ? (
            <AffiliationSearchField
              pt={theme.space.sm + theme.space.xs}
              id="benefitProvider"
              label={f({ id: 'register.benefitProvider' })}
              onNoMatch={setHasAffiliationSearchError}
              toggleMemberIdForm={setHideMemberIdForm}
              errorMessage={
                hasAffiliationSearchError
                  ? f({ id: 'noMatchFound' })
                  : undefined
              }
              setShowSkipLink={setShowSkipLink}
            />
          ) : (
            <>
              <Field
                name="affiliationId"
                type="hidden"
                component="input"
                validate={required}
              />
              <Field
                name="affiliationType"
                type="hidden"
                component="input"
                validate={required}
              />
            </>
          )}

          {!hideMemberIdForm ? (
            <FormSpy subscription={{ values: true }}>
              {({ values }) => {
                setInsuranceFormValues(values);
                return (
                  <Fragment>
                    <MemberId affiliationId={values.affiliationId} />
                    <AllowDepReg affiliationId={values.affiliationId} />
                    {values.isDependent === 'true' ? <PrimaryForm /> : null}
                  </Fragment>
                );
              }}
            </FormSpy>
          ) : null}

          <FormSubmitLoadingButton
            mt="md"
            data-testid="submitBtn"
            type="submit"
            size="large"
            disabled={
              !valid || submitting || hasAffiliationSearchError || disableSubmit
            }
            isLoading={submitting || disableSubmit}
            data-track-event
            data-track={JSON.stringify({
              controlName: 'submit-registerreview-button',
              controlText: f({ id: 'submitButton' }),
              attributes: [
                {
                  key: 'dependentChoice',
                  value: insuranceFormValues?.isDependent || '',
                },
              ],
            })}
          >
            <FormattedMessage id="submitButton" />
          </FormSubmitLoadingButton>
        </StyledForm>
      )}
    />
  );
};

export default withTheme(ReviewForm);
